import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';

/**
 * Read-only panel that lists values actually used across all recipes, splitting
 * them into "already in your list" and "not in your list (one-click Add)".
 */
const UsedInRecipes = ({ label, fetchInUse, curated, onAdd }) => {
    const [inUse, setInUse] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState('');

    useEffect(() => {
        let active = true;
        fetchInUse()
            .then((values) => { if (active) setInUse(values); })
            .catch(() => { if (active) setInUse([]); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [fetchInUse]);

    const curatedSet = new Set((curated || []).map((c) => String(c).toLowerCase()));
    const missing = inUse.filter((v) => !curatedSet.has(v.toLowerCase()));
    const present = inUse.filter((v) => curatedSet.has(v.toLowerCase()));

    const handleAdd = async (value) => {
        setAdding(value);
        try {
            await onAdd(value);
        } finally {
            setAdding('');
        }
    };

    return (
        <Box sx={{ mt: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {`${label} used in recipes`}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {`All ${label.toLowerCase()} found across every recipe. Add any that aren't in your list yet.`}
            </Typography>

            {loading && <Typography color="textSecondary">Scanning recipes…</Typography>}

            {!loading && missing.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Not in your list</Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                        {missing.map((v) => (
                            <Button
                                key={v}
                                size="small"
                                variant="outlined"
                                color="primary"
                                disabled={adding === v}
                                onClick={() => handleAdd(v)}
                            >
                                {`+ ${v}`}
                            </Button>
                        ))}
                    </Stack>
                </Box>
            )}

            {!loading && missing.length === 0 && (
                <Typography color="textSecondary" sx={{ mb: 2 }}>
                    Everything used in recipes is already in your list.
                </Typography>
            )}

            {!loading && present.length > 0 && (
                <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>Already in your list</Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                        {present.map((v) => <Chip key={v} label={v} size="small" />)}
                    </Stack>
                </Box>
            )}
        </Box>
    );
};

UsedInRecipes.propTypes = {
    label: PropTypes.string.isRequired,
    fetchInUse: PropTypes.func.isRequired,
    curated: PropTypes.arrayOf(PropTypes.string).isRequired,
    onAdd: PropTypes.func.isRequired,
};

export default UsedInRecipes;
